import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useCart } from "../lib/CartContext";
import { CreditCard, Smartphone, Wallet, Truck, Bitcoin, Apple, Check, X, Tag, Sparkles, QrCode, Landmark, Mail, Phone, MapPin } from "lucide-react";
import AddressBook from "../components/AddressBook";

const ICONS = { CreditCard, Smartphone, Wallet, Truck, Bitcoin, Apple, QrCode, Landmark };

export default function CheckoutPage() {
  const nav = useNavigate();
  const { items, total: cartSubtotal, count, refresh } = useCart();
  const [pickedAddrId, setPickedAddrId] = useState(null);
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [methods, setMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [walletBal, setWalletBal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [allCoupons, setAllCoupons] = useState([]);

  useEffect(() => {
    (async () => {
      const [pm, w, cs, ad] = await Promise.all([
        api.get("/payment-methods"),
        api.get("/wallet").catch(()=>({data:{balance:0}})),
        api.get("/coupons").catch(()=>({data:[]})),
        api.get("/buyer/addresses").catch(()=>({data:[]})),
      ]);
      setMethods(pm.data);
      setWalletBal(w.data.balance || 0);
      setAllCoupons(cs.data || []);
      setSavedAddrs(ad.data || []);
      const def = (ad.data || []).find(a => a.is_default);
      if (def) setPickedAddrId(def.addr_id);
      else if ((ad.data || []).length) setPickedAddrId(ad.data[0].addr_id);
    })();
  }, []);

  // Refresh address list when the user adds a new one inline.
  const reloadAddresses = async () => {
    try {
      const { data } = await api.get("/buyer/addresses");
      setSavedAddrs(data || []);
      // If the user just created their first address it'll be default.
      if (!pickedAddrId && data?.length) {
        const def = data.find(a => a.is_default) || data[0];
        setPickedAddrId(def.addr_id);
      }
    } catch { /* ignore */ }
  };
  // Pick the live picked address from the list — used in the order payload.
  const pickedAddr = savedAddrs.find(a => a.addr_id === pickedAddrId);

  // Seller-configured payment methods: per-store fetch, intersect across
  // all stores in the cart. The buyer can only pick methods every store
  // in their cart accepts (otherwise we'd need split-payment plumbing).
  const [sellerMethodsByStore, setSellerMethodsByStore] = useState({});
  const cartStoreIds = useMemo(() => {
    const ids = new Set();
    items.forEach(it => { if (it.product?.store_id) ids.add(it.product.store_id); });
    return [...ids];
  }, [items]);
  useEffect(() => {
    if (!cartStoreIds.length) { setSellerMethodsByStore({}); return; }
    (async () => {
      const out = {};
      await Promise.all(cartStoreIds.map(async (sid) => {
        try {
          const { data } = await api.get(`/checkout/payment-methods`, { params: { store_id: sid } });
          out[sid] = data;
        } catch { out[sid] = { methods: [], contact: null, store_name: "" }; }
      }));
      setSellerMethodsByStore(out);
    })();
  }, [cartStoreIds.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const intersectedSellerMethods = useMemo(() => {
    const stores = Object.values(sellerMethodsByStore);
    if (!stores.length) return [];
    let common = null;
    for (const s of stores) {
      const ids = new Set((s.methods || []).map(m => m.id));
      common = common === null ? ids : new Set([...common].filter(x => ids.has(x)));
    }
    if (!common || common.size === 0) return [];
    // Take labels from any store (they're identical across stores)
    const first = stores[0].methods.filter(m => common.has(m.id));
    return first;
  }, [sellerMethodsByStore]);

  // Single-store cart? Surface that store's full per-method config (qr image, bank details, contact)
  const singleStoreCfg = (cartStoreIds.length === 1) ? sellerMethodsByStore[cartStoreIds[0]] : null;
  const useSellerMethods = intersectedSellerMethods.length > 0;

  // Pick a sane initial method when seller methods load
  useEffect(() => {
    if (useSellerMethods) {
      const ids = intersectedSellerMethods.map(m => m.id);
      if (!ids.includes(paymentMethod)) setPaymentMethod(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSellerMethods, intersectedSellerMethods.length]);

  const subtotal = cartSubtotal;
  const walletAvail = useWallet ? Math.min(walletBal, Math.max(0, subtotal - discount)) : 0;
  const finalTotal = Math.max(0, subtotal - discount - walletAvail);
  const rewardsToEarn = +(finalTotal * 0.05).toFixed(2);

  const applyCoupon = async (codeOverride) => {
    const code = (codeOverride || coupon || "").trim();
    if (!code) return;
    setCouponMsg("");
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setAppliedCoupon(data.coupon);
      setDiscount(data.discount);
      setCoupon(code);
      setCouponMsg(`${code} applied — saved $${data.discount.toFixed(2)}`);
    } catch (ex) {
      setAppliedCoupon(null); setDiscount(0);
      setCouponMsg(ex.response?.data?.detail || "Invalid coupon");
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null); setDiscount(0); setCoupon(""); setCouponMsg("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedAddr) {
      setErr("Please pick or add a shipping address.");
      return;
    }
    setBusy(true); setErr("");
    try {
      // Map address-book entry → checkout payload shape (legacy fields)
      const addressPayload = {
        name: pickedAddr.name || "",
        line1: pickedAddr.line1 || "",
        line2: pickedAddr.line2 || "",
        city: pickedAddr.city || "",
        state: pickedAddr.state || "",
        country: pickedAddr.country || "",
        zip: pickedAddr.zip || pickedAddr.postal_code || "",
        phone: pickedAddr.phone || "",
      };
      const { data } = await api.post("/checkout", {
        address: addressPayload,
        addr_id: pickedAddr.addr_id,
        payment_method: paymentMethod,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        use_wallet: useWallet,
      });
      await refresh();
      nav(`/orders/${data.order_id}`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Checkout failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="pb-24 md:pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mb-6">Checkout<span className="text-[#D4FF00]">.</span></h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {/* Address picker — saved addresses with default pre-selected.
              Buyer can also "+ Add new address" inline from here, which
              persists to the address book so future checkouts auto-fill. */}
          <div className="card p-5">
            <div className="font-display font-bold uppercase text-sm mb-3">Shipping address</div>
            <AddressBookCheckoutPicker
              selectedId={pickedAddrId}
              onSelect={(a) => setPickedAddrId(a.addr_id)}
              onAddressChange={reloadAddresses}/>
          </div>

          {/* Payment method */}
          <div className="card p-5">
            <div className="font-display font-bold uppercase text-sm mb-3">Payment method</div>
            {useSellerMethods ? (
              <SellerMethodPicker
                methods={intersectedSellerMethods}
                selected={paymentMethod}
                onSelect={setPaymentMethod}
                singleStoreCfg={singleStoreCfg}
                multiStore={cartStoreIds.length > 1}
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {methods.map(m => {
                  const Icon = ICONS[m.icon] || CreditCard;
                  const active = paymentMethod === m.id;
                  return (
                    <button key={m.id} type="button"
                      onClick={()=>setPaymentMethod(m.id)}
                      data-testid={`pm-${m.id}`}
                      className={`p-3 rounded-2xl border text-left transition ${active?"border-[#D4FF00] bg-[#D4FF00]/10":"border-[#3D2845] hover:border-[#D4FF00]/50"}`}>
                      <Icon size={18} className={active?"text-[#D4FF00]":"text-white"}/>
                      <div className="text-xs font-bold mt-2">{m.label}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Seller contact preview (shown for single-store carts so the
              buyer knows whom they're paying before placing the order). */}
          {singleStoreCfg?.contact && (
            <div className="card p-5" data-testid="checkout-seller-contact">
              <div className="font-display font-bold uppercase text-sm mb-3">
                Seller contact · {singleStoreCfg.contact.store_name || singleStoreCfg.store_name}
              </div>
              <div className="grid sm:grid-cols-3 gap-2 text-sm">
                {singleStoreCfg.contact.email && (
                  <div className="flex items-start gap-2"><Mail size={14} className="text-[#D4FF00] mt-0.5 shrink-0"/><span className="break-all">{singleStoreCfg.contact.email}</span></div>
                )}
                {singleStoreCfg.contact.phone && (
                  <div className="flex items-start gap-2"><Phone size={14} className="text-[#D4FF00] mt-0.5 shrink-0"/><span>{singleStoreCfg.contact.phone}</span></div>
                )}
                {singleStoreCfg.contact.pickup_address && (
                  <div className="flex items-start gap-2"><MapPin size={14} className="text-[#D4FF00] mt-0.5 shrink-0"/><span>{singleStoreCfg.contact.pickup_address}</span></div>
                )}
              </div>
            </div>
          )}

          {/* Coupon */}
          <div className="card p-5">
            <div className="font-display font-bold uppercase text-sm mb-3 flex items-center gap-1"><Tag size={14}/> Coupon</div>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-[#D4FF00]/10 border border-[#D4FF00] rounded-xl px-4 py-3">
                <div>
                  <div className="text-xs text-[#A1A1AA] uppercase font-bold">Applied</div>
                  <div className="font-display font-bold text-[#D4FF00]">{appliedCoupon.code}</div>
                  <div className="text-xs">{appliedCoupon.title}</div>
                </div>
                <button type="button" onClick={removeCoupon} data-testid="coupon-remove-btn" className="text-[#FF007F] hover:text-white"><X size={16}/></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input className="input-dark" placeholder="Enter code" value={coupon} onChange={e=>setCoupon(e.target.value.toUpperCase())} data-testid="coupon-input"/>
                <button type="button" onClick={()=>applyCoupon()} data-testid="coupon-apply-btn" className="btn-secondary px-4">Apply</button>
              </div>
            )}
            {couponMsg && <div className={`text-xs mt-2 ${appliedCoupon?"text-[#D4FF00]":"text-[#FF007F]"}`}>{couponMsg}</div>}

            {!appliedCoupon && allCoupons.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] mb-2">Try one</div>
                <div className="flex gap-2 flex-wrap">
                  {allCoupons.map(c => (
                    <button key={c.code} type="button"
                      onClick={()=>applyCoupon(c.code)}
                      data-testid={`coupon-suggest-${c.code}`}
                      className="chip">
                      <Sparkles size={12} className="text-[#FF007F]"/> {c.code}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Wallet usage */}
          <div className="card p-5">
            <div className="font-display font-bold uppercase text-sm mb-3 flex items-center gap-1"><Wallet size={14}/> Use wallet balance</div>
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="font-bold text-sm">Apply ${walletBal.toFixed(2)} from wallet</div>
                <div className="text-xs text-[#A1A1AA]">Earn 5% back as rewards on every order.</div>
              </div>
              <input type="checkbox" checked={useWallet} onChange={e=>setUseWallet(e.target.checked)}
                data-testid="use-wallet-toggle"
                className="w-5 h-5 accent-[#D4FF00]" disabled={walletBal <= 0}/>
            </label>
          </div>
        </div>

        {/* Summary */}
        <aside className="card p-5 h-fit lg:sticky lg:top-24">
          <div className="font-display font-bold uppercase text-sm mb-4">Order summary</div>
          <div className="space-y-2 mb-4 text-sm max-h-48 overflow-y-auto">
            {items.map((it, i) => (
              <div key={`${it.product_id}-${it.size || "nosize"}-${i}`} className="flex gap-2 items-center text-xs">
                <img src={it.product?.images?.[0]} className="w-10 h-10 object-cover rounded-lg"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold line-clamp-1">{it.product?.name}</div>
                  <div className="text-[#A1A1AA]">{it.size || "—"} · ×{it.quantity}</div>
                </div>
                <div className="font-bold">{fmtMoney(it.product?.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#3D2845] pt-3 space-y-1.5 text-sm">
            <Row label="Subtotal" value={fmtMoney(subtotal)}/>
            {discount > 0 && <Row label={`Coupon ${appliedCoupon?.code || ""}`} value={`-${fmtMoney(discount)}`} accent="#D4FF00"/>}
            {walletAvail > 0 && <Row label="Wallet" value={`-${fmtMoney(walletAvail)}`} accent="#00F0FF"/>}
            <Row label="Shipping" value="FREE" accent="#D4FF00"/>
          </div>
          <div className="border-t border-[#3D2845] pt-3 mt-3 flex justify-between font-display font-black text-2xl">
            <span>Total</span>
            <span className="text-[#D4FF00]">{fmtMoney(finalTotal)}</span>
          </div>
          {rewardsToEarn > 0 && (
            <div className="text-xs text-[#FF007F] font-bold mt-1 text-right">+{fmtMoney(rewardsToEarn)} rewards back</div>
          )}
          <button data-testid="place-order-btn" type="submit" disabled={busy || items.length === 0} className="btn-primary w-full mt-4">
            {busy ? "Placing…" : `Place order · ${fmtMoney(finalTotal)}`}
          </button>
          {err && <div className="text-sm text-[#FF007F] mt-3">{err}</div>}
          <p className="text-[10px] text-[#A1A1AA] mt-3 text-center">Payment is <b>MOCKED</b> in this demo.</p>
        </aside>
      </form>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#A1A1AA]">{label}</span>
      <span style={accent ? { color: accent } : {}} className="font-bold">{value}</span>
    </div>
  );
}

function SellerMethodPicker({ methods, selected, onSelect, singleStoreCfg, multiStore }) {
  const sel = methods.find(m => m.id === selected);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {methods.map(m => {
          const Icon = ICONS[m.icon] || CreditCard;
          const active = selected === m.id;
          return (
            <button key={m.id} type="button"
              onClick={() => onSelect(m.id)}
              data-testid={`pm-seller-${m.id}`}
              className={`p-3 rounded-2xl border text-left transition ${active?"border-[#D4FF00] bg-[#D4FF00]/10":"border-[#3D2845] hover:border-[#D4FF00]/50"}`}>
              <Icon size={18} className={active?"text-[#D4FF00]":"text-white"}/>
              <div className="text-xs font-bold mt-2">{m.label}</div>
            </button>
          );
        })}
      </div>
      {multiStore && (
        <p className="text-[11px] text-[#A1A1AA] mt-3">
          Showing methods accepted by every store in your cart.
        </p>
      )}
      {/* Per-method instructions for single-store carts */}
      {singleStoreCfg && sel && (() => {
        const detailed = singleStoreCfg.methods.find(m => m.id === sel.id);
        if (!detailed) return null;
        if (sel.id === "cod") {
          return <Hint testid="pm-detail-cod">{detailed.note || "Pay rider in cash on delivery."}</Hint>;
        }
        if (sel.id === "qr") {
          return (
            <div className="mt-3 p-4 rounded-xl bg-[#261534] border border-[#3D2845]" data-testid="pm-detail-qr">
              <div className="flex items-start gap-3">
                {detailed.qr_image_url && (
                  <img src={detailed.qr_image_url} alt="QR" className="w-24 h-24 rounded-lg bg-white p-2 object-contain"/>
                )}
                <div className="flex-1 text-sm">
                  {detailed.qr_label && <div className="font-bold">{detailed.qr_label}</div>}
                  {detailed.handle && (
                    <div className="text-[#D4FF00] font-mono-display text-xs mt-1">{detailed.handle}</div>
                  )}
                  {detailed.note && (
                    <div className="text-[#A1A1AA] text-xs mt-2">{detailed.note}</div>
                  )}
                  <div className="text-[10px] text-[#FFA500] uppercase tracking-widest font-bold mt-2">
                    Order will move to processing once seller confirms payment.
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (sel.id === "bank") {
          return (
            <div className="mt-3 p-4 rounded-xl bg-[#261534] border border-[#3D2845] text-sm grid gap-1" data-testid="pm-detail-bank">
              {detailed.account_name && <div><span className="text-[#A1A1AA]">Account name: </span><span className="font-bold">{detailed.account_name}</span></div>}
              {detailed.account_number_masked && <div><span className="text-[#A1A1AA]">Account no: </span><span className="font-mono-display">{detailed.account_number_masked}</span></div>}
              {detailed.ifsc_swift && <div><span className="text-[#A1A1AA]">IFSC / SWIFT: </span><span className="font-mono-display">{detailed.ifsc_swift}</span></div>}
              {detailed.note && <div className="text-[#A1A1AA] text-xs mt-1">{detailed.note}</div>}
              <div className="text-[10px] text-[#FFA500] uppercase tracking-widest font-bold mt-1">
                Order will move to processing once seller confirms payment.
              </div>
            </div>
          );
        }
        if (sel.id === "gateway") {
          return <Hint testid="pm-detail-gateway">
            Pay securely via {detailed.provider || "Stripe"}. <span className="text-[#FF007F] font-bold">MOCKED</span> in this demo — auto-confirmed.
          </Hint>;
        }
        return null;
      })()}
    </div>
  );
}

function Hint({ children, testid }) {
  return (
    <p className="text-[11px] text-[#A1A1AA] mt-3 p-3 rounded-xl bg-[#261534] border border-[#3D2845]"
      data-testid={testid}>{children}</p>
  );
}


// Lightweight wrapper around <AddressBook/> tuned for inline checkout —
// renders the saved-address picker + inline "+ Add new address" form,
// and bubbles a refresh up so we can repopulate the picker once the
// buyer adds something new.
function AddressBookCheckoutPicker({ selectedId, onSelect, onAddressChange }) {
  const [, force] = useState(0);
  return (
    <AddressBook
      selectedId={selectedId}
      onSelect={(a) => { onSelect(a); onAddressChange?.(); force(x => x + 1); }}
    />
  );
}
